import React, { Component } from "react";
import "./NameStampSearch.css";
import { NameStampSearchResults } from "./NameStampSearchResults";
import { LocalityStampSearchResults } from "./LocalityStampSearchResults";
import queryString from "query-string";
import { navigate } from "@reach/router";
import { FormattedMessage, FormattedHTMLMessage } from "react-intl";
import { parseLang, constructApiUrl } from "../utils";
import { Helmet } from "react-helmet";
import { SearchForm } from "../components/SearchForm";
import Select from "../components/Select";
import { nameStampTypeOptions } from "../constants";
import Input from "../components/Input";
import MunicipalitySelect from "../components/MunicipalitySelect";
import { booleanOptions } from "../constants";

export class NameStampSearch extends Component {
  initialState = {
    lastSearch: "",
    isInUse: {},
    selectedMunicipalities: [],
    selectedNameStampType: {},
    page: 0,
    pageSize: 10,
    nameStampText: "",
    companyText: "",
    nameStampSortColumn: "Text",
    nameStampSortDirection: "asc",
    localityStampSortColumn: "Number",
    localityStampSortDirection: "asc",
    // these are not reflected in query params
    totalRows: 0,
    nameStampSearchResults: null,
    localityStampSearchResults: null,
    searchingNameStamps: false,
    searchingLocalityStamps: false,
    searchError: false,
  };

  constructor(props) {
    super(props);
    this.state = {
      ...this.initialState,
      municipalities: [],
    };
  }

  searchResultNameStampRef = React.createRef();
  searchResultLocalityNameStampRef = React.createRef();

  componentDidMount() {
    const { location } = this.props;
    const query = queryString.parse(location.search);

    const isInUse = booleanOptions.find(o => o.value === query.u);

    const selectedNameStampType = nameStampTypeOptions.find(o => o.value === query.nst);

    // set initial local state from query params
    this.setState({
      isInUse: isInUse || {},
      selectedNameStampType: selectedNameStampType || {},
      selectedMunicipalities: query.m || [],
      page: Number(query.p) || 0,
      nameStampText: query.ns || "",
      companyText: query.c || "",
      nameStampSortColumn: query.nsc || "Text",
      nameStampSortDirection: query.nsd || "asc",
      localityStampSortColumn: query.lsc || "municipality",
      localityStampSortDirection: query.lsd || "asc",
    });
  }

  resetForm = () => {
    this.setState(this.initialState);
    this.resetQueryString();
  };

  onNameStampSearch = () => {
    if(this.searchResultNameStampRef.current) {
      this.searchResultNameStampRef.current.scrollIntoView();
    }
  };

  onLocalityNameStampSearch = () => {
    if(this.searchResultLocalityNameStampRef.current) {
      this.searchResultLocalityNameStampRef.current.scrollIntoView();
    }
  };

  isFormValid = () => {
    return true;
  };

  buildNameStampQueryString = () => {
    return queryString.stringify({
      nameStampText: this.state.nameStampText,
      nameStampType: this.state.selectedNameStampType.value,
      municipality: this.state.selectedNamestampMunicipalities,
      companyText: this.state.companyText,
      page: this.state.page,
      pageSize: this.state.pageSize,
      order: `${
        !this.state.nameStampSortColumn || this.state.nameStampSortDirection === "asc" ? "" : "-"
      }${this.state.nameStampSortColumn}`,
    });
  };

  buildLocalityStampQueryString = () => {
    return queryString.stringify({
      isInUse: this.state.isInUse.value,
      municipality: this.state.selectedLocalityNamestampMunicipalities,
      page: this.state.page,
      pageSize: this.state.pageSize,
      order: `${
        !this.state.localityStampSortColumn || this.state.localityStampSortDirection === "asc"
          ? ""
          : "-"
      }${this.state.localityStampSortColumn}`,
    });
  };

  // updates new query parameters to current url
  navigateWithQuery = query => {
    const { location, uri } = this.props;
    const stringified = queryString.stringify({
      ...queryString.parse(location.search),
      ...query,
    });
    navigate(`${uri}?${stringified}`, { replace: true });
  };

  // this is called when user selects municipality from multiselect
  municipalityLocalityNamestampSelected = nextValues => {
    const queryValues = nextValues.map(value => value.value);
    this.setState({ selectedLocalityNamestampMunicipalities: queryValues }, () => {
      this.navigateWithQuery({ m: queryValues });
    });
  };

  municipalityNamestampSelected = nextValues => {
    const queryValues = nextValues.map(value => value.value);
    this.setState({ selectedNamestampMunicipalities: queryValues }, () => {
      this.navigateWithQuery({ m: queryValues });
    });
  };

  nameStampTypeSelected = selectedValue => {
    const nameStampType = selectedValue.value;
    this.setState({ selectedNameStampType: selectedValue }, () => {
      this.navigateWithQuery({ nst: nameStampType });
    });
  };

  setNameStampText = event => {
    const nameStampText = event.currentTarget.value;
    this.setState({ nameStampText }, () => {
      this.navigateWithQuery({ ns: nameStampText });
    });
  };

  setCompanyText = event => {
    const companyText = event.currentTarget.value;
    this.setState({ companyText }, () => {
      this.navigateWithQuery({ c: companyText });
    });
  };

  changeNameStampPage = nextPage => {
    this.setState({ page: nextPage }, () => {
      this.navigateWithQuery({ p: nextPage });
      this.fetchNameStamps();
    });
  };

  changeLocalityStampPage = nextPage => {
    this.setState({ page: nextPage }, () => {
      this.navigateWithQuery({ p: nextPage });
      this.fetchLocalityStamps();
    });
  };

  namestampIsInUseChanged = selectedValue => {
    const namestampIsInUse = selectedValue.value;
    this.setState({ isInUse: selectedValue }, () => {
      this.navigateWithQuery({ isInUse: namestampIsInUse });
    });
  };

  nameStampSortChanged = sortColumn => {
    const sortDirection = this.state.nameStampSortDirection === "asc" ? "desc" : "asc";

    if (this.state.nameStampSortColumn === sortColumn) {
      this.setState({ nameStampSortDirection: sortDirection }, () => {
        this.navigateWithQuery({ nsd: sortDirection });
        this.fetchNameStamps();
      });
    } else {
      this.setState(
        {
          nameStampSortColumn: sortColumn,
          nameStampSortDirection: sortDirection,
        },
        () => {
          this.navigateWithQuery({ nsc: sortColumn, nsd: sortDirection });
          this.fetchNameStamps();
        }
      );
    }
  };

  localityStampSortChanged = sortColumn => {
    const sortDirection = this.state.localityStampSortDirection === "asc" ? "desc" : "asc";
    if (this.state.localityStampSortColumn === sortColumn) {
      this.setState({ localityStampSortDirection: sortDirection }, () => {
        this.navigateWithQuery({ lsd: sortDirection });
        this.fetchLocalityStamps();
      });
    } else {
      this.setState(
        {
          localityStampSortColumn: sortColumn,
          localityStampSortDirection: sortDirection,
        },
        () => {
          this.navigateWithQuery({ lsc: sortColumn, lsd: sortDirection });
          this.fetchLocalityStamps();
        }
      );
    }
  };

  resetQueryString = () => {
    const { location, uri } = this.props;
    const query = queryString.parse(location.search);
    const lang = query.lang || null;
    const uriLang = lang ? `?lang=${lang}` : "";

    navigate(uri + uriLang);
  };

  fetchNameStamps = async setPage => {
    await this.setState(state => ({
      ...state,
      page: Number.isInteger(setPage) ? setPage : state.page,
      searchingNameStamps: !state.searchingNameStamps,
      nameStampSearchResults: null,
      localityStampSearchResults: null,
    }));

    if (Number.isInteger(setPage)) this.navigateWithQuery({ p: setPage });

    const apiUrl = process.env.REACT_APP_SEARCH_NAMESTAMPS;
    this.onNameStampSearch();
    try {
      const query = this.buildNameStampQueryString();
      const response = await fetch(constructApiUrl(apiUrl, query));
      const responseJSON = await response.json();
      const { totalRows, results } = responseJSON;
      this.setState({
        nameStampSearchResults: results,
        totalRows: totalRows,
        searchingNameStamps: false,
        searchError: false,
      });
    } catch (error) {
      this.setState({
        nameStampSearchResults: [],
        totalRows: 0,
        searchingNameStamps: false,
        searchError: true,
      });
    }
  };

  fetchLocalityStamps = async setPage => {
    await this.setState(state => ({
      ...state,
      page: Number.isInteger(setPage) ? setPage : state.page,
      searchingLocalityStamps: !state.searchingLocalityStamps,
      nameStampSearchResults: null,
      localityStampSearchResults: null,
    }));

    if (Number.isInteger(setPage)) this.navigateWithQuery({ p: setPage });

    const apiUrl = process.env.REACT_APP_SEARCH_LOCALITYSTAMPS;
    this.onLocalityNameStampSearch();

    try {
      const query = this.buildLocalityStampQueryString();
      const response = await fetch(constructApiUrl(apiUrl, query));
      const responseJSON = await response.json();
      const { totalRows, results } = responseJSON;
      this.setState({
        localityStampSearchResults: results,
        totalRows: totalRows,
        searchingLocalityStamps: false,
        searchError: false,
      });
    } catch (error) {
      this.setState({
        localityStampSearchResults: [],
        totalRows: 0,
        searchingLocalityStamps: false,
        searchError: true,
      });
    }
  };
  
  render() {
    const queryLang = parseLang(this.props.location.search);

    const nameStampSearchFields = [
      {
        label: "nameStampSearch.nameStampType",
        component: (
          <Select
            onChange={this.nameStampTypeSelected}
            options={nameStampTypeOptions}
            placeholder='nameStampSearch.nameStampTypePlaceholder'
            value={this.state.selectedNameStampType}
            hideSelectedOptions={false}
          />
        ),
      },
      {
        label: "nameStampSearch.nameStampCode",
        component: (
          <Input
            type='text'
            placeholder='nameStampSearch.nameStampCodePlaceholder'
            value={this.state.nameStampText}
            onChange={this.setNameStampText}
          />
        ),
      },
      {
        label: "common.companyOrPractitioner",
        component: (
          <Input
            type='text'
            placeholder='nameStampSearch.companySearchPlaceholder'
            value={this.state.companyText}
            onChange={this.setCompanyText}
          />
        ),
      },
      {
        label: "common.municipalities",
        component: (
          <MunicipalitySelect
            selectedMunicipalities={this.state.selectedNamestampMunicipalities}
            onChange={this.municipalityNamestampSelected}
            maxSelections={5}
            locale={queryLang}
          />
        ),
      },
    ];

    const localityStampSearchFields = [
      {
        label: "nameStampSearch.isInUse",
        component: (
          <Select
            onChange={this.namestampIsInUseChanged}
            options={booleanOptions}
            placeholder='common.choose'
            value={this.state.isInUse}
            hideSelectedOptions={false}
          />
        ),
      },
      {
        label: "localityStampSearch.municipalities",
        component: (
          <MunicipalitySelect
            selectedMunicipalities={this.state.selectedLocalityNamestampMunicipalities}
            onChange={this.municipalityLocalityNamestampSelected}
            maxSelections={5}
            locale={queryLang}
          />
        ),
      },
    ];

    return (
      <div className='RefrigerationSearch'>
        <FormattedMessage id='nameStampSearch.mainHeader'>
          {title => <Helmet title={title} />}
        </FormattedMessage>
        <p>
          <FormattedHTMLMessage
            id='nameStampSearch.overview.p1'
          />
        </p>
        <p>
          <FormattedMessage id='nameStampSearch.overview.p2' />
        </p>
        <p>
          <FormattedHTMLMessage
            id='nameStampSearch.overview.p3'
          />
        </p>
        <p>
          <FormattedHTMLMessage
            id='nameStampSearch.overview.p4'
          />
        </p>
        <p>
          <FormattedMessage id='nameStampSearch.overview.p5' />
        </p>
        <FormattedMessage tagName='h1' id='nameStampSearch.header' />

        <SearchForm
          fetch={this.fetchNameStamps}
          searchFields={nameStampSearchFields}
          isFormValid={this.isFormValid}
          searching={this.state.searchingNameStamps}
          resetForm={this.resetForm}
        />
        <FormattedMessage tagName='h1' id='localityStampSearch.header' />

        <SearchForm
          fetch={this.fetchLocalityStamps}
          searchFields={localityStampSearchFields}
          isFormValid={this.isFormValid}
          searching={this.state.searchingLocalityStamps}
          resetForm={this.resetForm}
        />

        <NameStampSearchResults
          changePage={this.changeNameStampPage}
          currentPage={this.state.page}
          pageSize={this.state.pageSize}
          results={this.state.nameStampSearchResults}
          totalRows={this.state.totalRows}
          searching={this.state.searchingNameStamps}
          searchError={this.state.searchError}
          sortColumn={this.state.nameStampSortColumn}
          sortDirection={this.state.nameStampSortDirection}
          updateSort={this.nameStampSortChanged}
          locale={queryLang}
          municipalities={this.state.municipalities}
          searchResultNameStampRef={this.searchResultNameStampRef}
        />
        <LocalityStampSearchResults
          changePage={this.changeLocalityStampPage}
          currentPage={this.state.page}
          pageSize={this.state.pageSize}
          results={this.state.localityStampSearchResults}
          totalRows={this.state.totalRows}
          searching={this.state.searchingLocalityStamps}
          searchError={this.state.searchError}
          sortColumn={this.state.localityStampSortColumn}
          sortDirection={this.state.localityStampSortDirection}
          updateSort={this.localityStampSortChanged}
          locale={queryLang}
          municipalities={this.state.municipalities}
          searchResultLocalityNameStampRef={this.searchResultLocalityNameStampRef}
        />
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/NameStampSearch/NameStampSearch.js